<template>
    <app-layout>
        <page-header :title="title" />
        <row>
            <column md="6">
                <jet-form-section type="decent" @submitted="handleSubmit($event)">
                    <template #title>{{ trans('locations.info.title') }} </template>
                    <template #form>
                        <jet-input
                            v-model="form.name"
                            :autofocus="true"
                            :invalidFeedback="form.errors.name"
                            :label="nameLabel"
                            :placeholder="trans('locations.name.placeholder')"
                            :required="true"
                            name="name"
                        />
                        <p v-if="translationHintLanguage" class="small mb-3 text-muted">
                            {{ trans('locations.attributes.translation_hint', { language: translationHintLanguage }) }}
                        </p>
                        <jet-input
                            v-model="form.attributes.icaoCode"
                            :invalidFeedback="form.errors.attributes?.icaoCode"
                            :label="trans('locations.icao_code.label')"
                            :placeholder="trans('locations.icao_code.placeholder')"
                            :required="true"
                            name="icao_code"
                        />
                        <jet-input
                            v-model="form.attributes.iataCode"
                            :invalidFeedback="form.errors.attributes?.iataCode"
                            :label="trans('locations.iata_code.label')"
                            :placeholder="trans('locations.iata_code.placeholder')"
                            :required="true"
                            name="iata_code"
                        />
                        <jet-input
                            v-model="form.attributes.countryCodeAlpha2"
                            :invalidFeedback="form.errors.attributes?.countryCodeAlpha2"
                            :label="trans('locations.country_code_alpha2.label')"
                            :placeholder="trans('locations.country_code_alpha2.placeholder')"
                            :required="true"
                            name="country_code_2"
                        />
                        <jet-input
                            v-model="form.attributes.countryCodeAlpha3"
                            :invalidFeedback="form.errors.attributes?.countryCodeAlpha3"
                            :label="trans('locations.country_code_alpha3.label')"
                            :placeholder="trans('locations.country_code_alpha3.placeholder')"
                            :required="true"
                            name="country_code_3"
                        />
                        <jet-select
                            v-model="form.attributes.timezone"
                            :enable-search="true"
                            :invalidFeedback="form.errors.attributes?.timezone"
                            :label="trans('locations.timezone.label')"
                            :options="timezones"
                            :required="true"
                            name="timezone"
                        />
                        <div class="d-flex align-items-center mt-3 flex-wrap" style="gap: 12px">
                            <jet-button type="button" variant="secondary" @click="attributesModalShow = true">
                                {{ trans('locations.attributes.manage') }}<span v-if="managedCount"> ({{ managedCount }})</span>
                            </jet-button>
                            <span v-if="needsFrench" class="small text-warning">{{ trans('locations.attributes.missing_french') }}</span>
                        </div>
                    </template>

                    <template #actions />
                </jet-form-section>
            </column>
            <column class="d-flex flex-column" md="6">
                <users-list />
            </column>
        </row>
        <row>
            <column>
                <location-structure
                    :adjacent-line-options="adjacentLineOptions"
                    :definitions="attributeDefinitions"
                    :location-id="location?.id"
                    :locations="childLocations"
                    @change="changeSet = $event"
                />
            </column>
        </row>
        <delete-location-modal />
        <location-attribute-modal
            :definitions="attributeDefinitions"
            :level="0"
            :model-value="form.managedAttributes"
            :show="attributesModalShow"
            @close="attributesModalShow = false"
            @save="saveAttributes"
        />
        <jet-form-buttons
            :cancel-route="route('locations.index')"
            :disabled="!form.isDirty && changeSet.length === 0"
            :has-changes="hasChanges"
            :is-processing="form.processing"
            :is-update-form="isUpdateForm"
            @submit="handleSubmit"
        />
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetSelect from '@/Jetstream/Select.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetFormButtons from '@/Jetstream/FormButtons.vue';
import { toTimezoneSelectOption } from '@/Utils/timezone';
import MultiSelectDropdown from '@/Components/MultiSelectDropdown.vue';
import PageHeader from '@/Components/PageHeader.vue';
import Card from '@/Components/Card.vue';
import JetButton from '@/Jetstream/Button.vue';
import Column from '@/Components/Column.vue';
import UsersList from '@/Pages/Locations/UsersList.vue';
import ConfirmedDelete from '@/Components/ConfirmedDelete.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import DeleteLocationModal from '@/Pages/Locations/DeleteLocationModal.vue';
import LocationStructure from '@/Pages/Locations/LocationStructure.vue';
import LocationAttributeModal from '@/Components/LocationAttributeModal.vue';
import { getLabelForLocale } from '@/Utils/getLabelForLocale';
import StickyBottomSheet from '@/Components/StickyBottomSheet.vue';
import UnsavedChangesModal from '@/Pages/Locations/UnsavedChangesModal.vue';
import { useUnsavedChangesWarning } from '@/Utils/UseUnsavedChangesWarning';

export default {
    components: {
        UnsavedChangesModal,
        StickyBottomSheet,
        LocationAttributeModal,
        LocationStructure,
        DeleteLocationModal,
        DialogModal,
        ConfirmedDelete,
        UsersList,
        Column,
        JetButton,
        Card,
        PageHeader,
        MultiSelectDropdown,
        AppLayout,
        JetFormButtons,
        JetInput,
        JetFormSection,
        JetSelect,
    },

    data() {
        return {
            changeSet: [],
            removeOnBeforeListener: null,
            handleFullPageRefresh: null,
            forceLeavePage: false,
            attributesModalShow: false,
            form: this.$inertia.form({
                parentId: null,
                name: undefined,
                managedAttributes: {},
                attributes: {
                    iataCode: undefined,
                    icaoCode: undefined,
                    countryCodeAlpha2: undefined,
                    countryCodeAlpha3: undefined,
                    timezone: undefined,
                },
            }),
        };
    },

    computed: {
        title() {
            return this.isUpdateForm ? this.trans('locations.edit.title') : this.trans('locations.create.title');
        },
        location() {
            return this.$page.props.location;
        },
        childLocations() {
            return this.$page.props.locationWithChildren?.children || [];
        },
        adjacentLineOptions() {
            return this.$page.props.locationWithChildren?.adjacentLineOptions || [];
        },
        timezones() {
            return this.$page.props.timezones.data.map(toTimezoneSelectOption);
        },
        attributeDefinitions() {
            return this.$page.props.attributeDefinitions || [];
        },
        hasTranslations() {
            return this.attributeDefinitions.some((definition) => typeof definition.key === 'string' && definition.key.startsWith('name_'));
        },
        nameLabel() {
            const base = this.trans('locations.name.label');
            return this.hasTranslations ? `${base} (${getLabelForLocale(this.$page.props.fallback_locale)})` : base;
        },
        translationHintLanguage() {
            const locale = this.$page.props.locale;
            const fallback = this.$page.props.fallback_locale;
            if (!locale || locale === fallback) {
                return null;
            }
            // Only hint when the active locale actually has a translatable name slot.
            if (!this.attributeDefinitions.some((definition) => definition.key === `name_${locale}`)) {
                return null;
            }
            return getLabelForLocale(locale);
        },
        managedCount() {
            return Object.values(this.form.managedAttributes || {}).filter((value) => value !== '' && value != null).length;
        },
        needsFrench() {
            return this.attributeDefinitions.some((definition) => definition.key === 'name_fr') && !this.form.managedAttributes?.name_fr;
        },
        isUpdateForm() {
            return !!this.location;
        },
        hasChanges() {
            return this.changeSet.length > 0 || this.form.isDirty;
        },
    },

    mounted() {
        this.form = this.$inertia.form({
            parentId: this.location?.parentId || null,
            name: this.location?.name,
            managedAttributes: { ...(this.location?.managedAttributes || {}) },
            attributes: {
                iataCode: this.location?.iataCode,
                icaoCode: this.location?.icaoCode,
                countryCodeAlpha2: this.location?.countryCodeAlpha2,
                countryCodeAlpha3: this.location?.countryCodeAlpha3,
                timezone: this.location?.timezone?.identifier,
            },
        });

        if (this.isUpdateForm) {
            this.$inertia.visit(this.route('locations.edit', this.location), {
                preserveScroll: true,
                preserveState: true,
                only: ['locationWithChildren'],
            });
        }

        useUnsavedChangesWarning({
            hasChanges: () => this.form.isDirty || this.form.processing,
            handleSubmit: this.handleSubmit,
        });
    },

    methods: {
        saveAttributes({ managedAttributes }) {
            this.form.managedAttributes = { ...managedAttributes };
            this.attributesModalShow = false;
        },
        transformData(data) {
            data.children = this.changeSet || [];
            data.attributes.icaoCode = data.attributes.icaoCode?.toUpperCase();
            data.attributes.iataCode = data.attributes.iataCode?.toUpperCase();
            data.attributes.countryCodeAlpha2 = data.attributes.countryCodeAlpha2?.toUpperCase();
            data.attributes.countryCodeAlpha3 = data.attributes.countryCodeAlpha3?.toUpperCase();
            return data;
        },
        handleSubmit: async function () {
            if (this.isUpdateForm) {
                return this.updateLocation();
            } else {
                return this.createLocation();
            }
        },
        createLocation: async function () {
            return new Promise((resolve, reject) => {
                this.form.transform(this.transformData).post(this.route('locations.store'), {
                    onSuccess: () => resolve(true),
                    onError: () => resolve(false),
                });
            });
        },
        updateLocation: async function () {
            return new Promise((resolve, reject) => {
                this.form.transform(this.transformData).put(
                    this.route('locations.update', {
                        location: this.location,
                    }),
                    {
                        preserveScroll: true,
                        only: ['location', 'locationWithChildren'],
                        onSuccess: () => resolve(true),
                        onError: () => resolve(false),
                    },
                );
            });
        },
    },
};
</script>
